import { action, computed, makeObservable, observable, when } from 'mobx';
import { RootStore } from './root';
import { ROUTES } from '../routes';
import { OPEN_WIX_APP_URL } from '../utils/constants';

const EMAIL_VERIFICATION_RESULT_DEBUG_CASE = 'emailVerificationResult';
const SUCCESS_ERROR_CODE = '0';
const CROSS_ACCOUNT_ERROR_CODE = '9992';

/**
 * Backing store for the email-confirmation landing page. The verification
 * result arrives as window globals; the page is reached from a backend URL
 * so we self-navigate to it once the result is present.
 */
export class EmailVerificationResultStore {
  public errorCode: string;
  public tokenEmail: string;
  public sessionEmail: string;
  private readonly rootStore: RootStore;
  private urlParams = new URLSearchParams(window.location.search);

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.errorCode = window.__EMAIL_VERIFICATION_ERROR_CODE__ ?? '';
    this.tokenEmail = window.__EMAIL_VERIFICATION_TOKEN_EMAIL__ ?? '';
    this.sessionEmail = window.__EMAIL_VERIFICATION_SESSION_EMAIL__ ?? '';

    makeObservable(this, {
      errorCode: observable,
      tokenEmail: observable,
      sessionEmail: observable,
      isSuccess: computed,
      isCrossAccount: computed,
      isExpiredOrInvalid: computed,
      onResend: action.bound,
      onCrossAccountLogin: action.bound,
      onGoHome: action.bound,
    });

    when(
      () => this.rootStore.isLoaded,
      () => {
        if (
          this.enableAccess &&
          this.rootStore.navigationStore.currentRoute !==
            ROUTES.EMAIL_VERIFICATION_RESULT
        ) {
          this.rootStore.navigationStore.navigate(
            ROUTES.EMAIL_VERIFICATION_RESULT,
          );
        }
      },
    );
  }

  // allow the page if there's a real error code, OR if the URL has ?debugCase=verifyEmail (the dev hook).
  get enableAccess(): boolean {
    return (
      !!this.errorCode ||
      this.urlParams.get('debugCase') === EMAIL_VERIFICATION_RESULT_DEBUG_CASE
    );
  }

  get isSuccess(): boolean {
    return this.errorCode === SUCCESS_ERROR_CODE;
  }

  get isCrossAccount(): boolean {
    return this.errorCode === CROSS_ACCOUNT_ERROR_CODE;
  }

  get isExpiredOrInvalid(): boolean {
    return !this.isSuccess && !this.isCrossAccount;
  }

  public onResend() {
    const { navigationStore, displayStore } = this.rootStore;
    // The post-login OTP flow doesn't run on mobile, so instead of resending we
    // send mobile users to the Wix app, where they can confirm their email from
    // account settings.
    if (displayStore.isMobile) {
      navigationStore.redirect(OPEN_WIX_APP_URL);
      return;
    }
    navigationStore.redirect(
      `https://users.${navigationStore.baseDomain}/signin/post-login/confirm-email?debugCase=confirm-email`,
    );
  }

  public onCrossAccountLogin() {
    this.rootStore.navigationStore.goToMainLogin({ email: this.tokenEmail });
  }

  public onGoHome() {
    const { navigationStore } = this.rootStore;
    navigationStore.redirect(`https://www.${navigationStore.baseDomain}`);
  }
}
